import {
  Component,
  ElementRef,
  EventEmitter, Inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MmAlertService } from '@metromarkets/components';

import { environment } from '~env/environment';
import { AdvancedFormUtil } from '../../utils/advanced-form.util';
import { ADVANCED_FORM_BASE_CONFIG } from '../../constants/advanced-form.constant';
import { IAdvancedFormConfig, IAdvancedFormEvent, IAdvancedFormSection } from '../../models/advanced-form.model';
import { IAsyncValidationError } from '../../models/advanced-form-validation.model';
import { FormErrorHandler } from '../../classes/form-error-handler.class';
import { REGISTRATION_WITH_HORECA_MANDATORY } from '~app/modules/authorization/constants/registration-base.constants';
import { Observable } from 'rxjs';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { APP_USER_IDAM_REGISTER_ROUTE, FeatureToggleEnum } from '~shared/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckEmailService } from '~app/modules/authorization/services/check.email.service';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { getCountryDomainCode } from '~server/utils/country-detection.util';

@Component({
  selector: 'app-advanced-form',
  templateUrl: './advanced-form.component.html',
  styleUrls: ['./advanced-form.component.scss'],
})
export class AdvancedFormComponent implements OnInit, OnChanges {
  @Input() config: IAdvancedFormConfig;
  @Output() events = new EventEmitter<IAdvancedFormEvent>();
  @ViewChild('form', { read: ElementRef }) formElement: ElementRef;
  @ViewChild('inputTextElement') inputTextField: ElementRef;

  public formGroup: FormGroup;
  public formSectionList: IAdvancedFormSection[];
  public formErrorHandler: FormErrorHandler;
  public legalText: string;
  public isSubmitting = false;
  public isFormInitialized = false;
  public hasFieldWithMandatoryMark = false;
  public readonly selectElementBackground = {
    'background-image': `url(${environment.cdnBaseUrl()}/images/icons/chevron-down.svg)`,
  };
  public readonly mandatoryFieldMark = '*';
  public isNoSpacesFieldEnabled$: Observable<boolean> = this.activeFeaturesConfigcatService.isFeatureEnabled(
    FeatureToggleEnum.FF_TMP_NO_SPACES_FIELD
  );

  currentRoute = this.router.url;
  validatedEmailId = '';
  @Input() isFieldDisabled = false;

  constructor(
    private formBuilder: FormBuilder,
    private alertService: MmAlertService,
    private router: Router,
    private route: ActivatedRoute,
    private activeFeaturesConfigcatService: ActiveFeaturesConfigcatService,
    private checkEmailService: CheckEmailService,
    @Optional() @Inject(REQUEST) private webAppBuyerRequest: any) {
    this.checkEmailService.currentEmail$.pipe().subscribe((data) => {
      this.validatedEmailId = data;
    });
  }

  public ngOnInit(): void {
    if (this.config) {
      this.initializeForm();
    }
  }

  public ngOnChanges(): void {
    if (this.config && !this.isFormInitialized) {
      this.initializeForm();
      this.isFormInitialized = true;
    }
  }

  makeTextFieldEditable() {
    this.isFieldDisabled = false;
    this.inputTextField.nativeElement.focus();
  }

  public onSubmit(): void {
    if (this.isSubmitting) {
      return;
    }
    // @ts-ignore
    if (window.mandateHoReCaQuestion && this.formGroup.get('isHoreca')) {
      this.formGroup.get('isHoreca').setValidators(Object.values(REGISTRATION_WITH_HORECA_MANDATORY.isHoreca));
    }

    AdvancedFormUtil.validateForm(this.formGroup);

    if (this.formGroup.valid) {
      this.isSubmitting = true;
      this.emitEvent('submit');
      this.formErrorHandler.resetFormError();
    } else {
      const invalidElements = this.formElement.nativeElement.querySelectorAll('.ng-invalid');
      if (invalidElements.length) {
        const firstInvalidElement = invalidElements[0];

        if (AdvancedFormUtil.isElementOutOfViewport(firstInvalidElement)) {
          firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
        }
        this.emitValidationErrors();
      }
    }
  }

  public emitEvent(eventType: string): void {
    const callback = (asyncValidationError?: IAsyncValidationError) =>
      this.handleFormEventCallback(eventType, asyncValidationError);

    const event = AdvancedFormUtil.generateFormEvent(this.formGroup, eventType, callback);
    this.events.emit(event);
  }

  private initializeForm(): void {
    const config = { ...ADVANCED_FORM_BASE_CONFIG, ...this.config };
    const {
      formSectionList,
      legalText,
      validationRules,
      initialFormData,
      fieldsExtraOptions,
      formValidatorList,
      updateOn = 'change',
    } = config;
    this.formSectionList = AdvancedFormUtil.updateFormSectionList(formSectionList, fieldsExtraOptions, validationRules);

    this.hasFieldWithMandatoryMark = this.formSectionList.some(({ fieldList: fields = [] }) =>
      fields.some(({ hasRequiredMark }) => hasRequiredMark)
    );

    const fieldList = this.formSectionList.reduce((acc, formSection) => [...acc, ...formSection.fieldList], []);
    const formConfig = AdvancedFormUtil.generateFormConfig(fieldList, validationRules, initialFormData, updateOn);
    this.formGroup = this.formBuilder.group(formConfig, { validators: formValidatorList, updateOn });
    this.formErrorHandler = new FormErrorHandler(this.formGroup, this.alertService);
    this.legalText = legalText;

    if (this.formGroup.get('email') && this.currentRoute.includes(APP_USER_IDAM_REGISTER_ROUTE)) {
      this.formGroup.get('email').setValue(this.validatedEmailId);
    }
  }

  private handleFormEventCallback(eventType: string, asyncValidationError: IAsyncValidationError): void {
    this.isSubmitting = eventType === 'submit' ? false : this.isSubmitting;

    if (asyncValidationError) {
      this.formErrorHandler.handleAsyncValidationError(asyncValidationError);
    }
  }

  private emitValidationErrors() {
    this.events.emit({
      type: 'validation',
      payload: this.formGroup,
      done: () => {},
    });
  }

  checkWhetherFieldIsPrefilledFormControl(fieldName) {
    const market = getCountryDomainCode(this.webAppBuyerRequest);
    return AdvancedFormUtil.checkWhetherFieldIsPrefilledFormControl(fieldName, market);
  }
}
